import { View, Text } from 'react-native'
import React, { useEffect, useMemo, useState } from 'react'
import Button from '@mui/material/Button'
import MaterialTable, { Column } from 'material-table';
import {
   StylesProvider,
   createGenerateClassName
} from '@material-ui/styles';
import {
   Promotions,
   Vouchers
} from './cs-connect'
import {
   useParams,
   Link
} from 'react-router-dom'
import { Alert } from './utils/Alert.web';
import {
   LastDocType
} from 'firestar'
import lodash from 'lodash';
import { SectionPanel } from './utils/SectionPanel';
import {
   getDiff,
   flattenObject
} from 'firestar/lib/Utils'
import { useCasinoContext } from './containers/CasinoContextProvider';
import moment from 'moment';

var QRCode = require('qrcode.react');

const INITIAL_PAGESIZE = 15
const PAGESIZE = 10

const generateClassName = createGenerateClassName({
   productionPrefix: 'mt',
   seed: 'mt'
}); //To prevent material table from changing appbar style

export default function VouchersList() {

   const [vouchers, setVouchers] = useState<Vouchers.Voucher[]>([])
   const [showLoader, setShowLoader] = useState(true)
   const [lastDoc, setLastDoc] = useState<LastDocType>()
   const [isExporting, setIsExporting] = useState(false)
   const { casino } = useCasinoContext()
   const [promotion, setPromotion] = useState<Promotions.Promotion>()

   const { promotionId } = useParams() as {
      promotionId: string
   }

   useEffect(() => {
      if(promotion != null){
         fetchVouchers()
      }
   }, [promotion])

   useEffect(() => {
      fetchPromotion()
   }, [])

   const columns = useMemo<Column<Vouchers.Voucher>[]>(() => [
      {
         field: 'id',
         title: 'Id',
         editable: 'never'
      },
      {
         field: 'name',
         title: 'Name',
         editable: 'always'
      },
      {
         field: 'code',
         title: 'Code',
         editable: 'never',
         render: (v) => (
            <div>{v.code?.toUpperCase()}</div>
         )
      },
      {
         field: 'orderInfo.name',
         title: 'Status',
         editable: 'never'
      },
      {
         title: 'Created At',
         render: (v) => (
            <div>
               {moment(v.createdAt.toDate()).format('DD MMM YYYY • hh:mm A')}
            </div>
         )
      }
   ], [])

   return (
      <View style={{
         padding: 20
      }}>
         <View style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 15
         }}>
            <Text>{casino?.name ?? '•••'} / {promotion?.name ?? '•••'}</Text>
            <View style={{
               flexDirection: 'row',
               alignItems: 'center'
            }}>
               <Button
                  variant='outlined'
                  size='small'
                  disabled={isExporting || casino == null}
                  onClick={onClickExportCsv}>{isExporting ? 'Exporting...' : 'Export CSV'}</Button>
               <Link style={{
                  textDecoration: 'none',
                  marginLeft: 15
               }} to={`/promotions/${promotionId}/voucher-generator`}>Generate Vouchers</Link>
            </View>
         </View>
         <StylesProvider generateClassName={generateClassName}>
            <MaterialTable
               isLoading={showLoader}
               options={{
                  search: false,
                  pageSizeOptions: [PAGESIZE],
                  pageSize: PAGESIZE,
                  sorting: true,
                  filtering: false,
               }}
               detailPanel={renderDetail}
               editable={{
                  onRowUpdate: onRowUpdate,
                  onRowDelete: onRowDelete
               }}
               columns={columns}
               data={vouchers}
               title='Vouchers' />
         </StylesProvider>
      </View>
   )

   async function fetchPromotion(){
      setShowLoader(true)
      try {
         const response = await Promotions.getPromotionWithId(promotionId)
         if(response != null){
            setPromotion(response)
         }else{
            throw new Error("We could not find this promotion");  
         }
      } catch (error) {
         console.log('Error Getting Promotion',error)
         Alert.alert('Error Getting Promotion ',String(error))
         setShowLoader(false)
      }
   }

   async function onClickExportCsv() {
      if (casino == null) return
      setIsExporting(true)
      try {
         const response = await Vouchers.getAllVouchersForPromotion(casino.id, promotionId)
         const rows = response.data.map((voucher) => [
            voucher.code.toUpperCase(),
            voucher.orderInfo ? 'Claimed' : 'Unclaimed'
         ].join(','))
         const csv = ['Code,Status', ...rows].join('\n')
         const blob = new Blob([csv], { type: 'text/csv' })
         const url = URL.createObjectURL(blob)
         const link = document.createElement('a')
         link.href = url
         link.download = `vouchers-${promotion?.name ?? promotionId}.csv`
         link.click()
         URL.revokeObjectURL(url)
      } catch (error) {
         console.log('Error Exporting Vouchers', error)
         Alert.alert('Error Exporting Vouchers', String(error))
      }
      setIsExporting(false)
   }

   async function fetchVouchers() {
      setShowLoader(true)
      try {
         const response = await Vouchers.getVoucherForPromotion(
            casino?.id!,
            promotionId,
            {
               limit: INITIAL_PAGESIZE,
               lastDoc: lastDoc,
               orderBy: {
                  fieldPath: 'createdAt',
                  directionStr: 'desc'
               }
            }
         )
         setLastDoc(response.lastDoc)
         setVouchers(response.data)
      } catch (error) {
         console.log('Error Getting Vouchers', error)
         Alert.alert('Error Getting Vouchers', String(error))
      }
      setShowLoader(false)
   }

   function onRowUpdate(newData: Vouchers.Voucher) {
      return new Promise<void>(async (resolve, reject) => {
         try {

            const __vouchers = [...vouchers]
            const changedData = {} as Vouchers.Voucher
            let oldData : undefined | Vouchers.Voucher

            const findIndex = vouchers.findIndex((v) => v.id == newData.id)
            if(findIndex >= 0){
               oldData = __vouchers[findIndex]
            }
            columns.filter(m => m.editable == 'always' || m.editable == 'onUpdate')
            .forEach(m => {
               lodash.set(changedData, m.field ?? '', lodash.get(newData, m.field ?? '') ?? null)
            })

				const changes = getDiff(changedData, oldData)
            delete (changes as any).tableData;

            const flattenedChanges = flattenObject(changes)

            await Vouchers.update(casino.id, promotionId, newData.id, flattenedChanges)

            __vouchers[findIndex] = {
               ...oldData,
               ...changedData
            }
            setVouchers(__vouchers)
            resolve()
         } catch (error) {
            alert('Error Deleting Voucher' + String(error))
            reject(error)
         }
      })
   }

   function onRowDelete(newData: Vouchers.Voucher) {
      return new Promise<void>(async (resolve, reject) => {
         try {
            await Vouchers.deleteVoucher(casino.id, promotionId, newData.id)
            const findIndex = vouchers.findIndex((v) => v.id == newData.id)
            if(findIndex >= 0){
               const __vouchers = [...vouchers]
               __vouchers.splice(findIndex, 1)
               setVouchers(__vouchers)
            }
            resolve()
         } catch (error) {
            alert('Error Deleting Voucher' + String(error))
            reject(error)
         }
      })
   }

   function renderDetail(newData: Vouchers.Voucher) {
      return (
         <View style={{
            padding: 15
         }}>
            <SectionPanel
               title='QR Image'
               render={() => (
                  <View>
                     <QRCode value={'https://app.infigifts.com/vouchers/' + newData?.id} />
                  </View>
               )} />
         </View>
      )
   }
}